import type {
  CurrentAdminRbacPermissionsResponse,
  CurrentAdminRbacV2Grant,
  RbacV2AbilityTuple,
} from '@gozoki/api-types'

/**
 * Comptes de pointage (« tablette »).
 *
 * Ces comptes portent le rôle `pointage-boutique` (`read`/`add` `hr.clocking.clocking` et les
 * lectures RH qui alimentent la liste des collaborateurs) affecté sur LEUR boutique. Leur seul
 * écran est la tablette de pointage, d'où la redirection dédiée à la connexion — et, pour un compte
 * qui cumule ce rôle avec d'autres (poste ADV distanciel, #905), l'entrée « Pointage » du menu.
 *
 * Module pur (aucun import React ni CASL) : la règle se teste seule (`tablet-account.test.mjs`) et
 * ne dépend que du contrat `GET /rbac/me/permissions`.
 */

/** Ce que lisent ces règles : les grants v2 du contrat (`GET /rbac/me/permissions`). */
type GrantsV2Source = Pick<CurrentAdminRbacPermissionsResponse, 'grantsV2'>

/** Ressource des pointages dans la taxonomie v2. */
export const CLOCKING_RESOURCE = 'hr.clocking.clocking' as const

/** Exigence de l'écran de pointage : créer un pointage. */
export const CLOCKING_ADD_PERMISSION: RbacV2AbilityTuple = ['add', CLOCKING_RESOURCE]

/**
 * Ressources qu'un compte de pointage peut porter — et rien d'autre : les lectures RH servent à
 * afficher la liste des collaborateurs de la boutique sur la tablette (identité, fiche, poste et
 * boutiques d'affectation). C'est la composition exacte du rôle « Pointage boutique (tablette) »
 * en production : sans `hr.staff.position` et `hr.staff.assignments`, aucun compte tablette réel
 * n'était reconnu et plus personne n'était redirigé à la connexion (kanban #905).
 *
 * Toute autre ressource (dont `dashboard.dashboard.global-indicators`, `stores.store-list.store`,
 * `hr.planning.weekly-planning`) disqualifie le compte : un manager qui pointe aussi garde la
 * navigation normale du back-office et n'est pas enfermé sur la tablette.
 */
const TABLET_RESOURCES = new Set<string>([
  CLOCKING_RESOURCE,
  'hr.staff.identity',
  'hr.staff.collaborator',
  'hr.staff.position',
  'hr.staff.assignments',
])

/** Périmètre boutique d'un ensemble de grants : `all`, ou l'union de leurs boutiques. */
const grantsStoreScope = (grants: CurrentAdminRbacV2Grant[]): 'all' | number[] => {
  // Un grant dont le périmètre n'est pas sérialisé ne restreint rien : on ne déduit jamais une
  // restriction d'une information absente (le serveur, lui, tranche cible par cible).
  if (grants.some((grant) => grant.storeIds === undefined || grant.storeIds === 'all')) return 'all'

  return [...new Set(grants.flatMap((grant) => grant.storeIds as number[]))]
}

const isClockingAdd = (grant: CurrentAdminRbacV2Grant): boolean =>
  grant.resource === CLOCKING_RESOURCE && grant.action === 'add'

const clockingGrants = (contract: GrantsV2Source, action?: string): CurrentAdminRbacV2Grant[] =>
  contract.grantsV2.filter(
    (grant) =>
      grant.resource === CLOCKING_RESOURCE && (action === undefined || grant.action === action),
  )

/**
 * Le compte n'est-il QU'une tablette : il pointe (`add hr.clocking.clocking`) et ne porte aucune
 * ressource hors périmètre tablette ? Un tel compte n'a aucun autre écran du back-office.
 *
 * Contrat illisible, ou aucun grant v2 ⇒ `false`.
 */
export const isTabletOnlyAccount = (contract: GrantsV2Source | null): boolean =>
  contract !== null &&
  contract.grantsV2.some(isClockingAdd) &&
  contract.grantsV2.every((grant) => TABLET_RESOURCES.has(grant.resource))

/**
 * Boutique sur laquelle rediriger un compte de pointage à la connexion, ou `null` si le compte
 * n'est pas une tablette (ou si son périmètre ne désigne pas une boutique unique : `all` ou
 * plusieurs boutiques ⇒ on laisse la navigation normale choisir le premier écran autorisé).
 *
 * `contract` à `null` (réponse absente, en erreur ou illisible) ⇒ `null` : aucune redirection
 * spéciale, jamais de blocage.
 */
export const resolveTabletStoreId = (contract: GrantsV2Source | null): number | null => {
  // Un compte tablette pointe, et ne fait que ça : sans `add hr.clocking.clocking`, ce n'en est
  // pas un, et la moindre permission hors périmètre tablette rend la main au menu.
  if (contract === null || !isTabletOnlyAccount(contract)) return null

  const scope = grantsStoreScope(clockingGrants(contract))
  if (scope === 'all' || scope.length !== 1) return null

  return scope[0]
}

/**
 * Une affectation (un `adminRoleId`), prise seule, est « pointage tablette » quand elle crée des
 * pointages, ne porte que des ressources tablette, et que son périmètre est une liste explicite de
 * boutiques — jamais `all`, périmètre d'un rôle réseau, ni absent.
 */
const isTabletClockingAssignment = (grants: CurrentAdminRbacV2Grant[]): boolean =>
  grants.some(isClockingAdd) &&
  grants.every((grant) => TABLET_RESOURCES.has(grant.resource) && Array.isArray(grant.storeIds))

/**
 * Boutiques (triées) sur lesquelles le compte porte une affectation « pointage tablette » — vide
 * s'il n'en a aucune. C'est ce qui ouvre l'entrée « Pointage » du menu : elle s'adresse aux comptes
 * qui portent le rôle de la tablette, seul ou cumulé avec d'autres rôles (poste ADV distanciel),
 * pas aux managers, DR ou ADV qui peuvent pointer par un autre rôle — ceux-là passent par le
 * planning (décision #905).
 *
 * Les grants sont regroupés par affectation d'origine (`adminRoleId`) : deux rôles cumulés sur un
 * compte ne se contaminent pas. Un grant sans `adminRoleId` ne qualifie jamais — on ne déduit rien
 * d'une information absente. `contract` à `null` ⇒ vide.
 */
export const resolveTabletClockingStoreIds = (contract: GrantsV2Source | null): number[] => {
  if (contract === null) return []

  const grantsByAssignment = new Map<number, CurrentAdminRbacV2Grant[]>()
  for (const grant of contract.grantsV2) {
    if (grant.adminRoleId === undefined) continue
    const grants = grantsByAssignment.get(grant.adminRoleId) ?? []
    grants.push(grant)
    grantsByAssignment.set(grant.adminRoleId, grants)
  }

  const storeIds = new Set<number>()
  for (const grants of grantsByAssignment.values()) {
    if (!isTabletClockingAssignment(grants)) continue
    for (const grant of grants) {
      for (const storeId of grant.storeIds as number[]) storeIds.add(storeId)
    }
  }

  return [...storeIds].sort((left, right) => left - right)
}

/**
 * Le compte peut-il pointer dans cette boutique ? Sert à afficher « Accès refusé » plutôt que
 * l'erreur brute du serveur (`assertAdminClockingTarget`) quand une tablette ouvre l'URL d'une
 * autre boutique que la sienne.
 *
 * Ne répond `false` que sur une restriction EXPLICITE : contrat illisible, ou aucun grant
 * `add hr.clocking.clocking` ⇒ on ne bloque pas, le serveur reste l'autorité et la garde de
 * permission de l'écran a déjà tranché.
 */
export const canClockInStore = (contract: GrantsV2Source | null, storeId: number): boolean => {
  if (contract === null) return true

  const grants = clockingGrants(contract, 'add')
  if (grants.length === 0) return true

  const scope = grantsStoreScope(grants)
  if (scope === 'all') return true

  return scope.includes(storeId)
}
